import { useNavigate } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import { AdminHomeCard, AdminHomeCardGroup, AdminHomeCardGroups } from "../../../admin/components/AdminHomeCard"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import {
    SUPPLIER_ADMIN_ACCOUNT_COMPANY_ROUTE,
    SUPPLIER_ADMIN_ACCOUNT_GDPR_ROUTE,
    SUPPLIER_ADMIN_ACQUIRING_REPORTS_BALANCE_ROUTE,
    SUPPLIER_ADMIN_ACQUIRING_REPORTS_ACQUIRING_FEES_ROUTE,
    SUPPLIER_ADMIN_ACQUIRING_REPORTS_BANK_TRANSFERS_ROUTE,
    SUPPLIER_ADMIN_ACQUIRING_ROUTE,
    SUPPLIER_ADMIN_ADMINISTRATORS_CREATE_ROUTE,
    SUPPLIER_ADMIN_ADMINISTRATORS_ROUTE,
    SUPPLIER_ADMIN_CREATE_PRODUCT_NUMBERS_ROUTE,
    SUPPLIER_ADMIN_DEVICES_ROUTE,
    SUPPLIER_ADMIN_MERCHANTS_CREATE_ROUTE,
    SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE,
    SUPPLIER_ADMIN_POS_SETTINGS_ROUTE,
    SUPPLIER_ADMIN_PRODUCT_NUMBERS_ROUTE,
    SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_CREATE_ROUTE,
    SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE,
} from "../../../../constants/routes"
import { MatchEnvironment } from "../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../environment/envTypes"

const useStyles = makeStyles()((_theme) => ({
    page: {
        paddingTop: "3rem",
    },
}))

type Props = Readonly<{}>

export function SupplierAdminHomePage({}: Props) {
    const { classes } = useStyles()
    const navigate = useNavigate()

    return (
        <div className={classes.page}>
            <AdminHomeCardGroups>
                <LocalizedStrict id="supplier-admin-home-merchants-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Merchants">
                        <LocalizedStrict
                            id="supplier-admin-home-merchant-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="Merchants"
                                description="Manage merchants, which is physical locations to sell products."
                                onShowAllClick={() => navigate(`${SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE}`)}
                                onCreateClick={() => navigate(SUPPLIER_ADMIN_MERCHANTS_CREATE_ROUTE)}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="supplier-admin-home-devices-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="Devices"
                                description="Manage devices, such as POS, check-in screens, printers."
                                onShowAllClick={() => navigate(SUPPLIER_ADMIN_DEVICES_ROUTE)}
                            />
                        </LocalizedStrict>
                    </AdminHomeCardGroup>
                </LocalizedStrict>

                <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}>
                    <LocalizedStrict id="supplier-admin-home-erp-group" attrs={{ title: true }}>
                        <AdminHomeCardGroup title="ERP">
                            <LocalizedStrict
                                id="supplier-admin-home-product-numbers-card"
                                attrs={{ title: true, description: true }}
                            >
                                <AdminHomeCard
                                    title="Product numbers"
                                    description="Manage product numbers for ERP integration."
                                    onCreateClick={() => navigate(SUPPLIER_ADMIN_CREATE_PRODUCT_NUMBERS_ROUTE)}
                                    onShowAllClick={() => navigate(SUPPLIER_ADMIN_PRODUCT_NUMBERS_ROUTE)}
                                />
                            </LocalizedStrict>
                        </AdminHomeCardGroup>
                    </LocalizedStrict>
                </MatchEnvironment>

                <LocalizedStrict id="supplier-admin-home-acquiring-reports-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Reconciliation">
                        <LocalizedStrict
                            id="supplier-admin-home-balance"
                            attrs={{ title: true, description: true, buttonText: true }}
                        >
                            <AdminHomeCard
                                title="Balance"
                                description="Report showing total sales, fees and transfers for a period."
                                buttonText="Show"
                                onShowAllClick={() => navigate(SUPPLIER_ADMIN_ACQUIRING_REPORTS_BALANCE_ROUTE)}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="supplier-admin-home-acquiring-fees"
                            attrs={{ title: true, description: true, buttonText: true }}
                        >
                            <AdminHomeCard
                                title="Acquiring fees"
                                description="Report showing fees for acquiring. Useful for reconciliation."
                                buttonText="Show"
                                onShowAllClick={() => navigate(SUPPLIER_ADMIN_ACQUIRING_REPORTS_ACQUIRING_FEES_ROUTE)}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="supplier-admin-home-bank-transfers-card"
                            attrs={{ title: true, description: true, buttonText: true }}
                        >
                            <AdminHomeCard
                                title="Bank transfers"
                                description="Report showing transfers between your acquiring accounts and bank accounts."
                                buttonText="Show"
                                onShowAllClick={() => navigate(SUPPLIER_ADMIN_ACQUIRING_REPORTS_BANK_TRANSFERS_ROUTE)}
                            />
                        </LocalizedStrict>
                    </AdminHomeCardGroup>
                </LocalizedStrict>

                <LocalizedStrict id="supplier-admin-home-settings-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Settings">
                        <LocalizedStrict
                            id="supplier-admin-home-payment-acquiring-card"
                            attrs={{ title: true, description: true, buttonText: true }}
                        >
                            <AdminHomeCard
                                title="Payment acquiring"
                                description="Manage and check status of acquiring agreements."
                                buttonText="Show"
                                onShowAllClick={() => navigate(SUPPLIER_ADMIN_ACQUIRING_ROUTE)}
                            />
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="supplier-admin-home-terms-and-conditions-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="Terms and conditions"
                                description="Manage your sales terms and conditions towards your customers."
                                onShowAllClick={() => navigate(SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_ROUTE)}
                                onCreateClick={() => navigate(SUPPLIER_ADMIN_TERMS_AND_CONDITIONS_CREATE_ROUTE)}
                            />
                        </LocalizedStrict>
                        <MatchEnvironment
                            environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}
                        >
                            <LocalizedStrict
                                id="supplier-admin-home-pos-settings-card"
                                attrs={{ title: true, description: true }}
                            >
                                <AdminHomeCard
                                    title="POS settings"
                                    description="Manage POS settings."
                                    onShowAllClick={() => navigate(SUPPLIER_ADMIN_POS_SETTINGS_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>
                    </AdminHomeCardGroup>
                </LocalizedStrict>

                <LocalizedStrict id="supplier-admin-home-account-group" attrs={{ title: true }}>
                    <AdminHomeCardGroup title="Account">
                        <LocalizedStrict
                            id="supplier-admin-home-administrators-card"
                            attrs={{ title: true, description: true }}
                        >
                            <AdminHomeCard
                                title="Administrators"
                                description="Manage supplier and merchant administrators."
                                onShowAllClick={() => navigate(`${SUPPLIER_ADMIN_ADMINISTRATORS_ROUTE}?tab=supplier`)}
                                onCreateClick={() => navigate(SUPPLIER_ADMIN_ADMINISTRATORS_CREATE_ROUTE)}
                            />
                        </LocalizedStrict>
                        <MatchEnvironment
                            environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}
                        >
                            <LocalizedStrict
                                id="supplier-admin-home-company-account-card"
                                attrs={{ title: true, description: true, buttonText: true }}
                            >
                                <AdminHomeCard
                                    title="Company account"
                                    description="Manage your company account."
                                    buttonText="Show"
                                    onShowAllClick={() => navigate(SUPPLIER_ADMIN_ACCOUNT_COMPANY_ROUTE)}
                                />
                            </LocalizedStrict>
                        </MatchEnvironment>
                        <LocalizedStrict
                            id="supplier-admin-home-account-gdpr-card"
                            attrs={{ title: true, description: true, buttonText: true }}
                        >
                            <AdminHomeCard
                                title="GDPR"
                                description="General Data Protection Regulation"
                                buttonText="Show"
                                onShowAllClick={() => navigate(SUPPLIER_ADMIN_ACCOUNT_GDPR_ROUTE)}
                            />
                        </LocalizedStrict>
                    </AdminHomeCardGroup>
                </LocalizedStrict>
            </AdminHomeCardGroups>
        </div>
    )
}
